<script setup lang="ts">
    import { useI18n } from 'vue-i18n';
    import { InputRow, MultiCombobox } from '@/components/ui/form';
    import { Input } from '@/components/ui/input';
    import { Button } from '@/components/ui/button';
    import { Plus } from 'lucide-vue-next';
    import DeleteButton from '@/components/DeleteButton.vue';
    import { useVoucherAssigneeOptions } from '@/composables/useVoucherAssigneeOptions';
    import { computed } from 'vue';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import { splitAssigneeIds } from '@/utils/voucherAssignees';

    const { t } = useI18n();
    const selectedCompanyId = computed(() => usePage<SharedData>().props.auth.user.selected_company_id);
    const { assigneeOptions, isLoadingAssigneeOptions, dedupeAssigneeIds, toAssigneeIds } =
        useVoucherAssigneeOptions(selectedCompanyId.value);

    const props = defineProps<{
        form: any;
    }>();

    const fallbackAssigneeIds = computed({
        get: () => props.form.fallback_assignee_ids ?? [],
        set: (value: string[]) => {
            const deduped = dedupeAssigneeIds(value);
            props.form.fallback_assignee_ids = deduped;
            const { employee_ids, user_ids } = splitAssigneeIds(deduped);
            props.form.fallback_employee_ids = employee_ids;
            props.form.fallback_user_ids = user_ids;
        },
    });

    const addVoucherAssignmentRule = () => {
        props.form.voucher_assignment_rules.push({
            prompt: '',
            employee_ids: [],
            user_ids: [],
            assignee_ids: [],
        });
    };

    const removeVoucherAssignmentRule = (index: number) => {
        props.form.voucher_assignment_rules.splice(index, 1);
    };

    const updateRuleAssignees = (index: number, assigneeIds: string[]) => {
        const rule = props.form.voucher_assignment_rules[index];
        const deduped = dedupeAssigneeIds(assigneeIds);
        rule.assignee_ids = deduped;
        const { employee_ids, user_ids } = splitAssigneeIds(deduped);
        rule.employee_ids = employee_ids;
        rule.user_ids = user_ids;
    };

    defineExpose({
        addVoucherAssignmentRule,
        toAssigneeIds,
    });
</script>

<template>
    <div class="space-y-4">
        <InputRow
            :form="form"
            name="fallback_assignee_ids"
            label="vouchers.administrate.fallback"
        >
            <MultiCombobox
                v-model="fallbackAssigneeIds"
                :placeholder="t('vouchers.administrate.fallback')"
                :options="assigneeOptions"
                label-by="label"
                value-by="id"
                :loading="isLoadingAssigneeOptions"
            />
        </InputRow>

        <InputRow
            :form="form"
            name="voucher_assignment_rule_employees"
            label="vouchers.administrate.voucher_assignment_rule"
        >
            <div class="space-y-3">
                <div
                    v-for="(assignmentRule, index) in form.voucher_assignment_rules"
                    :key="index"
                    class="flex gap-3"
                >
                    <div class="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
                        <InputRow
                            :form="form"
                            name="voucher_assignment_rules.prompt"
                            :error-key="`voucher_assignment_rules.${index}.prompt`"
                        >
                            <Input
                                id="prompt"
                                v-model="assignmentRule.prompt"
                                :placeholder="
                                    t('vouchers.administrate.voucher_assignment_rules_prompt')
                                "
                            />
                        </InputRow>
                        <InputRow
                            :form="form"
                            name="voucher_assignment_rules.assignee_ids"
                            :error-key="`voucher_assignment_rules.${index}.employee_ids`"
                        >
                            <MultiCombobox
                                :model-value="assignmentRule.assignee_ids"
                                :placeholder="t('vouchers.administrate.fallback')"
                                :options="assigneeOptions"
                                label-by="label"
                                value-by="id"
                                :loading="isLoadingAssigneeOptions"
                                @update:model-value="updateRuleAssignees(index, $event)"
                            />
                        </InputRow>
                    </div>
                    <DeleteButton @click.prevent="removeVoucherAssignmentRule(index)" />
                </div>
            </div>
        </InputRow>

        <div class="flex justify-center">
            <Button
                variant="ghost"
                size="sm"
                @click.prevent="addVoucherAssignmentRule"
            >
                {{ t('vouchers.administrate.add_voucher_assignment_rule') }}
                <Plus class="text-accent size-3" />
            </Button>
        </div>
    </div>
</template>
